import { IndexedAsyncStorageCache } from '@/services/_shared/IndexedAsyncStorageCache';

interface CachedEpisodeDetails {
  episode_id: number;
  title: string;
  description: string;
  episode_position: number;
  season_position: number;
  duration: number;
  series_id: number;
  url_streaming_no_drm?: string;
  url_streaming_drm?: string;
  is_free?: boolean;
  contents_series: {
    title: string;
    url_covers: string;
  };
}

const EPISODE_DETAILS_CACHE_PREFIX = '@episode_details_';
const EPISODE_CACHE_INDEX_KEY = '@episode_details_index';
const EPISODE_DETAILS_TTL = 24 * 60 * 60 * 1000;

class EpisodeDetailsCacheService extends IndexedAsyncStorageCache<CachedEpisodeDetails> {
  constructor() {
    super({
      indexKey: EPISODE_CACHE_INDEX_KEY,
      logTag: '📝 [EPISODE-DETAILS-CACHE]',
      defaultTtlMs: EPISODE_DETAILS_TTL,
      quotaEvictionBatch: 50,
      maxQuotaRetries: 3,
    });
  }

  private generateCacheKey(campaignId: string, episodeId: number): string {
    return `${EPISODE_DETAILS_CACHE_PREFIX}${campaignId}_${episodeId}`;
  }

  async getEpisodeDetails(campaignId: string, episodeId: number): Promise<CachedEpisodeDetails | null> {
    return this.getRaw(this.generateCacheKey(campaignId, episodeId));
  }

  async getBulkEpisodeDetails(
    campaignId: string,
    episodeIds: number[]
  ): Promise<Map<number, CachedEpisodeDetails>> {
    await this.initialize();
    const results = new Map<number, CachedEpisodeDetails>();
    await Promise.all(
      episodeIds.map(async (episodeId) => {
        const details = await this.getEpisodeDetails(campaignId, episodeId);
        if (details) results.set(episodeId, details);
      })
    );
    return results;
  }

  async setEpisodeDetails(campaignId: string, episodeDetails: CachedEpisodeDetails): Promise<void> {
    await this.setRaw(this.generateCacheKey(campaignId, episodeDetails.episode_id), episodeDetails);
  }

  async setBulkEpisodeDetails(
    campaignId: string,
    episodeDetailsList: CachedEpisodeDetails[]
  ): Promise<void> {
    await Promise.all(episodeDetailsList.map((d) => this.setEpisodeDetails(campaignId, d)));
  }

  async remove(campaignId: string, episodeId: number): Promise<void> {
    await this.removeRaw(this.generateCacheKey(campaignId, episodeId));
  }
}

export const episodeDetailsCacheService = new EpisodeDetailsCacheService();
export type { CachedEpisodeDetails };
